// @ts-nocheck

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { flashMessageActionCreators } from '../../../actions';
import useMergeProps from '../../../hooks/useMergeProps';
import { __ } from '../../../i18n/l10n';
import { FlashMessageScopes, FlashMessageSeverities, FlashMessageTypes } from '../../../types/flashmessage';
import { registerMPAdventure } from '../actions/mp';
import { openMPView } from '../actions/routes';
import MPPrimaryButton from '../components/MPPrimaryButton';
import { MPRegisterAPIErrorCodes } from '../constants/api';
import MPEnrollmentButtonSelectors from '../selectors/specifics/MPEnrollmentButtonSelectors';

type MPEnrollmentButtonContainerProps = {
  className?: string;
  onRegisterRequested?: () => void;
  delay?: number;
  disabled?: boolean;
  onRegisterResponsed?: () => void;
};

const MPEnrollmentButtonContainer = ({
  onRegisterRequested = () => {},
  delay,
  disabled: forceDisabled,
  onRegisterResponsed = () => {},
  ...ownProps
}: MPEnrollmentButtonContainerProps) => {
  const dispatch = useDispatch();
  const disabled = useSelector(MPEnrollmentButtonSelectors.selectIsDisabled);

  const selectedProps = useMemo(() => ({ disabled: forceDisabled ?? disabled }), [forceDisabled, disabled]);

  const timeoutRef = useRef(null);

  const onClick = useCallback(() => {
    onRegisterRequested();

    const redirect = () => {
      dispatch(openMPView());
      timeoutRef.current = null;
    };

    const onRegisterSuccess = () => {
      if (delay) {
        timeoutRef.current = setTimeout(redirect, delay);
      } else {
        redirect();
      }
    };
    const onRegisterError = (e) => {
      let message = null;
      const { code: errorCode } = e ?? {};

      switch (errorCode) {
        case MPRegisterAPIErrorCodes.E_ALREADY_REGISTERED:
          message = __('!desc:userAlreadyOptedIn');
          break;
        case MPRegisterAPIErrorCodes.E_RMJ_30:
          message = __('!desc:%1$s ES RM J-30 warning', __('Warning:'));
          break;
        case MPRegisterAPIErrorCodes.E_USER_EXCLUDED:
          message = __('You are currently excluded from betting promotions in your %1$s', __('gaming limits'));
          break;
        default:
          message = __('Internal error');
          break;
      }

      onRegisterResponsed();

      if (message) {
        dispatch(
          flashMessageActionCreators.addMessage({
            scope: FlashMessageScopes.MP,
            severity: FlashMessageSeverities.ERROR,
            type: FlashMessageTypes.DISMISS_MANUAL,
            message: __('Your bonus code has been accepted'),
          }),
        );
      }
    };

    dispatch(
      registerMPAdventure({
        onSuccess: onRegisterSuccess,
        onError: onRegisterError,
      }),
    );
  }, [onRegisterRequested, dispatch, delay, onRegisterResponsed]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const otherProps = useMemo(
    () => ({
      onClick,
    }),
    [onClick],
  );

  const enhancedProps = useMergeProps({ ownProps, selectedProps, otherProps });

  return <MPPrimaryButton {...enhancedProps}>{__('missions:Opt in')}</MPPrimaryButton>;
};

export default MPEnrollmentButtonContainer;
